import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import {
  createEmailTemplate,
  deleteEmailTemplate,
  listEmailTemplates,
  updateEmailTemplate,
  ONBOARDING_TEMPLATE_VARIABLES,
  type EmailTemplate,
  type EmailTemplateUpsert,
} from '../lib/api';
import { WafDataGrid } from '../components/WafDataGrid';

interface EditorState {
  mode: 'create' | 'edit';
  id?: number;
  isSystem: boolean;
  key: string;
  name: string;
  description: string;
  subject: string;
  bodyHtml: string;
}

const EMPTY_EDITOR: EditorState = {
  mode: 'create',
  isSystem: false,
  key: '',
  name: '',
  description: '',
  subject: '',
  bodyHtml: '',
};

function extractApiError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: { message?: string; error?: string } } }).response?.data;
    if (data?.message) return data.message;
    if (data?.error) return data.error;
  }
  return err instanceof Error ? err.message : fallback;
}

export function EmailTemplates() {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [editor, setEditor] = useState<EditorState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<EmailTemplate | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return listEmailTemplates()
      .then(setRows)
      .catch((err) => setError(extractApiError(err, 'Failed to load templates')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => setEditor({ ...EMPTY_EDITOR });
  const openEdit = (t: EmailTemplate) =>
    setEditor({
      mode: 'edit',
      id: t.id,
      isSystem: t.is_system,
      key: t.key,
      name: t.name,
      description: t.description ?? '',
      subject: t.subject,
      bodyHtml: t.body_html,
    });

  const save = async () => {
    if (!editor) return;
    setBusy(true);
    setError(null);
    try {
      const payload: EmailTemplateUpsert = {
        name: editor.name,
        description: editor.description,
        subject: editor.subject,
        body_html: editor.bodyHtml,
      };
      if (editor.mode === 'create') {
        payload.key = editor.key;
        await createEmailTemplate(payload);
        setNotice(`Created template "${editor.name}".`);
      } else {
        await updateEmailTemplate(editor.id!, payload);
        setNotice(`Updated template "${editor.name}".`);
      }
      setEditor(null);
      await load();
    } catch (err) {
      setError(extractApiError(err, 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    setError(null);
    try {
      await deleteEmailTemplate(confirmDelete.id);
      setNotice(`Deleted template "${confirmDelete.name}".`);
      setConfirmDelete(null);
      await load();
    } catch (err) {
      setError(extractApiError(err, 'Delete failed'));
    } finally {
      setBusy(false);
    }
  };

  const columns = useMemo<GridColDef<EmailTemplate>[]>(
    () => [
      {
        field: 'key',
        headerName: 'Key',
        width: 180,
        renderCell: (p: GridRenderCellParams<EmailTemplate, string>) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <code>{p.value}</code>
            {p.row.is_system && <Chip size="small" label="system" variant="outlined" />}
          </Stack>
        ),
      },
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
      { field: 'subject', headerName: 'Subject', flex: 1.4, minWidth: 240 },
      {
        field: 'updated_at',
        headerName: 'Updated',
        width: 170,
        valueFormatter: (v: string) => (v ? new Date(v).toLocaleString() : ''),
      },
      {
        field: 'actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p: GridRenderCellParams<EmailTemplate>) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit">
              <Button size="small" onClick={() => openEdit(p.row)} sx={{ minWidth: 0, px: 1 }}>
                <EditOutlinedIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip title={p.row.is_system ? 'System templates cannot be deleted' : 'Delete'}>
              <span>
                <Button
                  size="small"
                  color="error"
                  disabled={p.row.is_system}
                  onClick={() => setConfirmDelete(p.row)}
                  sx={{ minWidth: 0, px: 1 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </Button>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    []
  );

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        flexWrap="wrap"
        useFlexGap
      >
        <Typography variant="h4" fontWeight={600}>
          Email templates
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add template
        </Button>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

      <Box sx={{ height: 'calc(100vh - 240px)', minHeight: 420 }}>
        <WafDataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          onRowClick={(p) => openEdit(p.row as EmailTemplate)}
          initialState={{ sorting: { sortModel: [{ field: 'key', sort: 'asc' }] } }}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Box>

      {/* Editor dialog with a live HTML preview pane. */}
      <Dialog open={editor !== null} onClose={() => setEditor(null)} maxWidth="lg" fullWidth fullScreen={fullScreen}>
        {editor && (
          <>
            <DialogTitle>{editor.mode === 'create' ? 'New email template' : `Edit "${editor.name}"`}</DialogTitle>
            <DialogContent dividers>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Stack spacing={2} sx={{ flex: 1 }}>
                  <TextField
                    label="Key"
                    size="small"
                    value={editor.key}
                    disabled={editor.mode === 'edit'}
                    onChange={(e) => setEditor({ ...editor, key: e.target.value })}
                    helperText={
                      editor.mode === 'edit'
                        ? 'The key is fixed once created.'
                        : 'Lowercase letters, numbers and hyphens, e.g. onboard-shortlisted.'
                    }
                  />
                  <TextField
                    label="Name"
                    size="small"
                    value={editor.name}
                    onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                  />
                  <TextField
                    label="Description (optional)"
                    size="small"
                    value={editor.description}
                    onChange={(e) => setEditor({ ...editor, description: e.target.value })}
                  />
                  <TextField
                    label="Subject"
                    size="small"
                    value={editor.subject}
                    onChange={(e) => setEditor({ ...editor, subject: e.target.value })}
                  />
                  <TextField
                    label="HTML body"
                    size="small"
                    multiline
                    minRows={8}
                    value={editor.bodyHtml}
                    onChange={(e) => setEditor({ ...editor, bodyHtml: e.target.value })}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Available variables (wrap in double braces, e.g. {'{{firstName}}'}):
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {ONBOARDING_TEMPLATE_VARIABLES.map((v) => (
                        <Chip key={v} size="small" variant="outlined" label={`{{${v}}}`} />
                      ))}
                    </Stack>
                  </Box>
                </Stack>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Preview
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 300, overflow: 'auto' }}>
                    <Typography variant="caption" color="text.secondary">
                      Subject
                    </Typography>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      {editor.subject || '(no subject)'}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    {/* Admin-only preview of our own template content. Placeholders
                        render literally here — they're substituted at send time. */}
                    <Box
                      sx={{ '& a': { color: 'primary.main' } }}
                      dangerouslySetInnerHTML={{ __html: editor.bodyHtml || '<em>(empty body)</em>' }}
                    />
                  </Paper>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditor(null)} disabled={busy}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={save}
                disabled={busy || !editor.key.trim() || !editor.name.trim() || !editor.subject.trim() || !editor.bodyHtml.trim()}
              >
                {editor.mode === 'create' ? 'Create' : 'Save changes'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete confirmation. */}
      <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth fullScreen={fullScreen}>
        <DialogTitle>Delete template?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This permanently deletes <strong>{confirmDelete?.name}</strong> (
            <code>{confirmDelete?.key}</code>). Any onboarding assignment using it will fail to send
            until reassigned.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={busy}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={doDelete} disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
